import { PrimitiveType, } from "@buildwithflux/models";
import { maxBy } from "lodash";
import { vec2 } from "../../../common";
import { TraceCorners } from "../../libs/traceCorners";
/** No shear supported! Please check if it's present beforehand */
export function approxBoundingCircle(primitive, transformDecomposed) {
    switch (primitive.type) {
        case PrimitiveType.Circle:
            return circleBoundingCircle(primitive, transformDecomposed);
        case PrimitiveType.Oblong:
            return oblongBoundingCircle(primitive, transformDecomposed);
        case PrimitiveType.Rectangle:
            return rectangleBoundingCircle(primitive, transformDecomposed);
        case PrimitiveType.RoundedRectangle:
            return roundedRectangleBoundingCircle(primitive, transformDecomposed);
        case PrimitiveType.Line:
            return lineBoundingCircle(primitive, transformDecomposed);
        case PrimitiveType.LineArc:
            return lineArcBoundingCircle(primitive, transformDecomposed);
        case PrimitiveType.ApproxPolygon:
            return approxPolygonBoundingCircle(primitive, transformDecomposed);
        default:
            return { centerX: 0, centerY: 0, radius: 0 };
    }
}
function circleBoundingCircle(primitive, transformDecomposed) {
    return transformBoundingCircle({ radius: primitive.radius, centerX: 0, centerY: 0 }, primitive.transform, transformDecomposed);
}
function oblongBoundingCircle(primitive, transformDecomposed) {
    return transformBoundingCircle({ radius: Math.max(primitive.width / 2, primitive.height / 2), centerX: 0, centerY: 0 }, primitive.transform, transformDecomposed);
}
function rectangleBoundingCircle(primitive, transformDecomposed) {
    const radius = Math.sqrt((primitive.width / 2) ** 2 + (primitive.height / 2) ** 2);
    return transformBoundingCircle({ radius, centerX: 0, centerY: 0 }, primitive.transform, transformDecomposed);
}
function roundedRectangleBoundingCircle(primitive, transformDecomposed) {
    const rectRadius = Math.sqrt((primitive.width / 2) ** 2 + (primitive.height / 2) ** 2);
    const smallestRadius = Math.min(primitive.radiusBottomLeft, primitive.radiusBottomRight, primitive.radiusTopLeft, primitive.radiusTopRight);
    const radiusRectDiff = Math.sqrt(smallestRadius ** 2 * 2) - smallestRadius;
    const radius = rectRadius - radiusRectDiff;
    return transformBoundingCircle({ radius, centerX: 0, centerY: 0 }, primitive.transform, transformDecomposed);
}
function lineBoundingCircle(primitive, transformDecomposed) {
    const minPointX = Math.min(primitive.startX, primitive.endX);
    const minPointY = Math.min(primitive.startY, primitive.endY);
    const maxPointX = Math.max(primitive.startX, primitive.endX);
    const maxPointY = Math.max(primitive.startY, primitive.endY);
    const centerX = (minPointX + maxPointX) / 2;
    const centerY = (minPointY + maxPointY) / 2;
    const radius = Math.sqrt((maxPointX - centerX) ** 2 + (maxPointY - centerY) ** 2) + primitive.thickness / 2;
    return transformBoundingCircle({ radius, centerX, centerY }, primitive.transform, transformDecomposed);
}
function lineArcBoundingCircle(primitive, transformDecomposed) {
    const { maxX, maxY, minX, minY } = TraceCorners.shapeBounds(primitive);
    const centerX = (maxX + minX) / 2;
    const centerY = (maxY + minY) / 2;
    const radius = Math.sqrt((maxX - centerX) ** 2 + (maxY - centerY) ** 2);
    return transformBoundingCircle({
        radius,
        centerX,
        centerY,
    }, primitive.transform, transformDecomposed);
}
function approxPolygonBoundingCircle(primitive, transformDecomposed) {
    if (primitive.points.length === 0) {
        // Is this the correct behavior?
        return transformBoundingCircle({ radius: 0, centerX: 0, centerY: 0 }, primitive.transform, transformDecomposed);
    }
    let centerPointX = primitive.points[0].x;
    let centerPointY = primitive.points[0].y;
    // eslint-disable-next-line @buildwithflux/no-array-foreach
    primitive.points.forEach((p) => {
        centerPointX += p.x;
        centerPointY += p.y;
    });
    const centerPoint = {
        x: centerPointX / primitive.points.length,
        y: centerPointY / primitive.points.length,
    };
    const radius = Math.sqrt(vec2.distanceSq(maxBy(primitive.points, (p) => vec2.distanceSq(p, centerPoint)), centerPoint));
    return transformBoundingCircle({ radius, centerX: 0, centerY: 0 }, primitive.transform, transformDecomposed);
}
function transformBoundingCircle(origCircle, transformMat, 
// The same as transformMat but decomposed
transformMatDecomposed) {
    const center = vec2.applyMatrix3({ x: origCircle.centerX, y: origCircle.centerY }, transformMat);
    const centerX = center.x;
    const centerY = center.y;
    // APPROX: To avoid the ellipse situation, uses the biggest one of the two scales (to remain a circle)
    const radius = origCircle.radius *
        Math.max(Math.abs(transformMatDecomposed.scale.x), Math.abs(transformMatDecomposed.scale.y));
    // Shear is ignored!
    return { centerX, centerY, radius };
}
export function boundingCircleDistance(bcA, bcB) {
    return Math.sqrt((bcA.centerX - bcB.centerX) ** 2 + (bcA.centerY - bcB.centerY) ** 2) - bcA.radius - bcB.radius;
}
//# sourceMappingURL=approxBoundingCircle.js.map